import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Eye, CheckCircle, Clock, Megaphone, FileText, ExternalLink } from "lucide-react";
import { getAuthenticatedFileUrl } from '../utils/fileUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ComunicazioniOperatore({ user }) {
  const [comunicazioni, setComunicazioni] = useState([]);
  const [selectedCom, setSelectedCom] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [filtroStato, setFiltroStato] = useState('tutte');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const comRes = await axios.get(`${API}/comunicazioni`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComunicazioni(comRes.data);
    } catch (err) {
      toast.error('Errore caricamento comunicazioni');
    }
  };

  const handleLeggi = async (com) => {
    setSelectedCom(com);
    setShowDialog(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/comunicazioni/${com.id}/segna-letto`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Errore marcatura comunicazione:', err);
    }
  };

  const isLetta = (com) => {
    return com.letture?.some(l => l.user_id === user.id) || false;
  };

  const totale = comunicazioni.length;
  const daLeggere = comunicazioni.filter(c => !isLetta(c)).length;
  const lette = comunicazioni.filter(c => isLetta(c)).length;

  const filtered = comunicazioni.filter(com => {
    if (filtroStato === 'tutte') return true;
    const read = isLetta(com);
    if (filtroStato === 'lette') return read;
    if (filtroStato === 'da_leggere') return !read;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="comunicazioni-operatore">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Comunicazioni</h2>
          <p className="text-xs text-gray-500">{totale} comunicazioni, {daLeggere} da leggere</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-1.5">
        {[
          { key: 'tutte', label: 'Tutte', count: totale, color: 'bg-gray-100 text-gray-700 border-gray-200' },
          { key: 'da_leggere', label: 'Da leggere', count: daLeggere, color: 'bg-amber-100 text-amber-700 border-amber-200' },
          { key: 'lette', label: 'Lette', count: lette, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFiltroStato(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              filtroStato === f.key ? f.color + ' shadow-sm' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm text-gray-500">Nessuna comunicazione</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(com => {
            const read = isLetta(com);
            return (
              <div
                key={com.id}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
                  read ? 'border-gray-100 bg-white' : 'border-cyan-200 bg-cyan-50/30'
                }`}
                onClick={() => handleLeggi(com)}
                data-testid={`comunicazione-${com.id}`}
              >
                <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                  read ? 'bg-gray-100' : 'bg-cyan-100'
                }`}>
                  <Megaphone className={`w-4 h-4 ${read ? 'text-gray-400' : 'text-cyan-600'}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className={`text-sm truncate ${read ? 'font-medium text-gray-700' : 'font-semibold text-gray-900'}`}>
                      {com.titolo}
                    </h3>
                    {!read && (
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-medium rounded shrink-0">
                        Nuova
                      </span>
                    )}
                    {read && (
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    {com.protocollo && <span className="font-mono text-cyan-600">{com.protocollo}</span>}
                    <span>{new Date(com.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    {com.tipologia_nome && (
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">{com.tipologia_nome}</span>
                    )}
                  </div>
                </div>

                <Button
                  size="sm"
                  variant={read ? "ghost" : "default"}
                  onClick={(e) => { e.stopPropagation(); handleLeggi(com); }}
                  className={`shrink-0 h-8 gap-1.5 text-xs ${read ? 'text-gray-500 hover:bg-gray-100' : 'bg-cyan-600 hover:bg-cyan-700'}`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  {read ? 'Rileggi' : 'Leggi'}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Reading Dialog */}
      {showDialog && selectedCom && (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center shrink-0 mt-0.5">
                  <Megaphone className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <DialogTitle className="text-base">{selectedCom.titolo}</DialogTitle>
                  <DialogDescription className="flex items-center gap-2 mt-0.5">
                    {selectedCom.protocollo && <span className="font-mono text-xs">{selectedCom.protocollo}</span>}
                    <span>Pubblicata il {new Date(selectedCom.created_at).toLocaleDateString('it-IT')}</span>
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <div
              className="prose prose-sm max-w-none text-sm mt-2"
              dangerouslySetInnerHTML={{ __html: selectedCom.contenuto }}
            />
            {selectedCom.link_esterno && (
              <a
                href={selectedCom.link_esterno}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-700 mt-3"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {selectedCom.link_esterno}
              </a>
            )}
            {selectedCom.file_pdf_url && (
              <a
                href={getAuthenticatedFileUrl(selectedCom.file_pdf_url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 mt-2"
              >
                <FileText className="w-3.5 h-3.5" />
                {selectedCom.file_pdf_nome || 'Scarica PDF'}
              </a>
            )}
            <DialogFooter className="mt-4">
              <Button onClick={() => setShowDialog(false)} variant="outline">Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
